/**
 * TrailDrawPage — full-screen satellite map for drawing game trail waypoints.
 *
 * Navigated to from GameTrailForm with route state:
 *   { pathGeoJSON?: GeoJSONLineString, centerCoords?: [lat, lng] }
 *
 * On save, navigates to `returnTo` (from route state) with:
 *   { trailResult: GeoJSONLineString }
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import { ChevronLeft } from 'lucide-react';

import { HuntMap } from '@/components/map/HuntMap';
import { useMap } from '@/hooks/useMap';
import type { GeoJSONLineString } from '@/types/waypoint';

// ─── Constants ─────────────────────────────────────────────────────────────────

const SOURCE_ID = 'trail-draw-source';
const LINE_LAYER_ID = 'trail-draw-line';

const TRAIL_COLOR = '#c8651a'; // ember-500 — matches --color-ember-500 in src/index.css

const TRAIL_LINE_PAINT: maplibregl.LinePaint = {
  'line-color': TRAIL_COLOR,
  'line-width': 3,
  'line-opacity': 0.85,
  'line-dasharray': [2, 1],
};

// ─── Waypoint marker element ───────────────────────────────────────────────────

function makeWaypointEl(index: number): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = `
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${TRAIL_COLOR};
    border: 1.5px solid #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 6px;
    font-weight: 700;
    color: #fff;
    font-family: 'DM Sans', system-ui, sans-serif;
    cursor: default;
  `;
  // SAFETY: index is a controlled integer, not user input
  el.textContent = String(index + 1);
  return el;
}

// ─── DrawingController — lives inside MapProvider ──────────────────────────────

interface DrawingControllerProps {
  waypoints: [number, number][];
  onVertexMove: (idx: number, lat: number, lng: number) => void;
}

export function DrawingController({ waypoints, onVertexMove }: DrawingControllerProps) {
  const map = useMap();
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const mountedRef = useRef(true);

  function removeLayers() {
    try {
      if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    } catch { /* map may be mid-destroy */ }
  }

  function removeMarkers() {
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];
  }

  const rebuild = useCallback(() => {
    if (!mountedRef.current) return;
    removeLayers();
    removeMarkers();

    if (waypoints.length === 0) return;

    if (waypoints.length >= 2) {
      const coords = waypoints.map(([lat, lng]) => [lng, lat]);

      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: coords },
          properties: {},
        },
      });

      map.addLayer({
        id: LINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        paint: TRAIL_LINE_PAINT,
      });
    }

    for (let i = 0; i < waypoints.length; i++) {
      const [lat, lng] = waypoints[i];
      const idx = i;
      const el = makeWaypointEl(idx);
      // Prevent click on a marker from bubbling to the map wrapper and adding a waypoint
      el.addEventListener('click', (e) => e.stopPropagation());
      const marker = new maplibregl.Marker({ element: el, draggable: true, anchor: 'center' })
        .setLngLat([lng, lat])
        .addTo(map);
      marker.on('dragend', () => {
        const pos = marker.getLngLat();
        onVertexMove(idx, pos.lat, pos.lng);
      });
      markersRef.current.push(marker);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypoints, onVertexMove]);

  useEffect(() => {
    mountedRef.current = true;
    map.on('style.load', rebuild);
    if (map.isStyleLoaded()) rebuild();

    return () => {
      mountedRef.current = false;
      map.off('style.load', rebuild);
      removeLayers();
      removeMarkers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rebuild]);

  return null;
}

// ClickHandler removed — click capture is handled by the React onClick wrapper
// around the map container (see mapWrapperRef / handleMapWrapperClick in TrailDrawPage)

// ─── Route state shape ────────────────────────────────────────────────────────

interface TrailDrawState {
  pathGeoJSON?: GeoJSONLineString;
  centerCoords?: [number, number];
  returnTo?: string;
  locationName?: string;
}

// ─── TrailDrawPage ─────────────────────────────────────────────────────────────

export function TrailDrawPage() {
  const { id: waypointId } = useParams<{ id: string }>();
  const reactLocation = useLocation();
  const navigate = useNavigate();

  const state = (reactLocation.state ?? {}) as TrailDrawState;
  const { pathGeoJSON: initialPath, centerCoords, returnTo, locationName } = state;

  // Parse existing path into waypoints on mount
  const [waypoints, setWaypoints] = useState<[number, number][]>(() => {
    if (initialPath?.coordinates) {
      return initialPath.coordinates.map(([lng, lat]) => [lat, lng]);
    }
    return [];
  });

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef<maplibregl.Map | null>(null);

  const hasEnoughWaypoints = waypoints.length >= 2;
  const hasAnyWaypoints = waypoints.length > 0;

  // Default center: property/location center or a sensible fallback
  const mapCenter: [number, number] = centerCoords ?? [39.5, -98.35];

  const handleMapClick = useCallback((lat: number, lng: number) => {
    setWaypoints((prev) => [...prev, [lat, lng]]);
  }, []);

  const handleVertexMove = useCallback((idx: number, lat: number, lng: number) => {
    setWaypoints((prev) => {
      const next = [...prev] as [number, number][];
      next[idx] = [lat, lng];
      return next;
    });
  }, []);

  function handleClear() {
    setWaypoints([]);
  }

  function handleSaveTrail() {
    if (!hasEnoughWaypoints) return;

    const pathGeoJSON: GeoJSONLineString = {
      type: 'LineString',
      coordinates: waypoints.map(([lat, lng]) => [lng, lat]),
    };

    const firstWaypoint = waypoints[0];

    if (returnTo) {
      navigate(returnTo, {
        state: { trailResult: pathGeoJSON, trailAnchor: firstWaypoint, locationName },
        replace: false,
      });
    } else {
      navigate(-1 as never);
    }
  }

  function handleBack() {
    const hadInitialPath = !!initialPath?.coordinates?.length;
    const clearedExisting = hadInitialPath && waypoints.length === 0;
    if (hasAnyWaypoints || clearedExisting) {
      setShowDiscardConfirm(true);
    } else {
      navigate(-1 as never);
    }
  }

  function handleDiscard() {
    setShowDiscardConfirm(false);
    navigate(-1 as never);
  }

  // Fit map to existing waypoints when map is ready
  function handleMapReady(map: maplibregl.Map) {
    mapRef.current = map;
    map.getCanvas().style.cursor = 'crosshair';
    setMapReady(true);

    if (waypoints.length >= 2) {
      const lngs = waypoints.map(([, lng]) => lng);
      const lats = waypoints.map(([lat]) => lat);
      map.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 60, animate: false },
      );
    }
  }

  // Suppress unused waypointId lint — used for route matching context
  void waypointId;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black"
      data-testid="trail-draw-page"
      data-map-ready={mapReady ? 'true' : undefined}
    >
      {/* ── Top bar ── */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-bark-900/90 backdrop-blur-sm flex items-center gap-3 px-4 py-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 12px)' }}
      >
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-1 text-[var(--color-text-secondary)] hover:text-white transition-colors"
          aria-label="Back"
        >
          <ChevronLeft size={20} />
          <span className="text-body-sm font-medium">Back</span>
        </button>

        <span className="text-body-sm font-semibold text-[var(--color-text-primary)] ml-2">Draw Trail</span>
      </div>

      {/* ── Full-screen map ── */}
      {/* data-testid allows tests to click with force:true, bypassing MapLibre's stopPropagation */}
      <div
        data-testid="trail-map-area"
        className="absolute inset-0"
        style={{ cursor: 'crosshair' }}
        onClick={(e) => {
          if (!mapRef.current) return;
          // Stop if the click came from a UI control (e.g. attribution toggle)
          if ((e.target as HTMLElement).closest('.maplibregl-ctrl')) return;
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          const lngLat = mapRef.current.unproject([e.clientX - rect.left, e.clientY - rect.top]);
          handleMapClick(lngLat.lat, lngLat.lng);
        }}
      >
        <HuntMap
          center={mapCenter}
          zoom={15}
          className="h-full w-full"
          initialStyle="satellite"
          onMapReady={handleMapReady}
        >
          <DrawingController waypoints={waypoints} onVertexMove={handleVertexMove} />
        </HuntMap>
      </div>

      {/* ── Instruction banner — hidden once ≥ 2 waypoints placed ── */}
      {!hasEnoughWaypoints && (
        <div
          className="absolute left-4 right-4 z-10 bg-bark-800/80 backdrop-blur-sm rounded-lg px-4 py-3"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 80px)' }}
          data-testid="trail-instruction-banner"
        >
          <p className="text-body-sm text-[var(--color-text-primary)] text-center">
            Tap the map to mark points along the trail path.
          </p>
        </div>
      )}

      {/* ── Bottom action bar ── */}
      <div
        className="absolute left-0 right-0 z-10 bg-bark-900/90 backdrop-blur-sm flex items-center gap-3 px-4 py-3"
        style={{ bottom: 0, paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
      >
        <button
          type="button"
          onClick={handleClear}
          data-testid="trail-clear-button"
          className="flex-1 py-2.5 rounded-md text-body-sm font-medium text-[var(--color-text-muted)] border border-[var(--color-border)] hover:border-[var(--color-border-subtle)] hover:text-[var(--color-text-secondary)] transition-colors"
        >
          Clear
        </button>

        <button
          type="button"
          onClick={handleSaveTrail}
          disabled={!hasEnoughWaypoints}
          data-testid="trail-save-button"
          className="flex-1 py-2.5 rounded-md text-body-sm font-semibold bg-[var(--color-accent)] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--color-accent-hover)] transition-colors"
        >
          Save Trail
        </button>
      </div>

      {/* ── Discard confirmation ── */}
      {showDiscardConfirm && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="mx-6 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-6 flex flex-col gap-4 shadow-xl max-w-xs w-full">
            <p className="text-body-md font-semibold text-[var(--color-text-primary)]">Discard trail points?</p>
            <p className="text-body-sm text-[var(--color-text-placeholder)]">
              Your placed waypoints will not be saved.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDiscardConfirm(false)}
                className="flex-1 py-2.5 rounded-md text-body-sm font-medium text-[var(--color-text-secondary)] border border-[var(--color-border)] hover:border-[var(--color-border-subtle)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDiscard}
                className="flex-1 py-2.5 rounded-md text-body-sm font-semibold text-white bg-red-700 hover:bg-red-600 transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
